import { FC } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import FileCheckboxList from '../FileCheckboxList';
import { contactSelectKey, toggleExclusivePath } from '../ProcessReportModal.utils';
import { useProcessReportModalContext } from '../ProcessReportModalContext';
import WorkboardDropboxPicker from '../WorkboardDropboxPicker';

const ProcessReportInputsPanel: FC = () => {
  const {
    companyName,
    useS3Fallback,
    contacts,
    isLoadingContacts,
    selectedContactKey,
    setSelectedContactKey,
    initialPickerPrefix,
    selectedModelPath,
    selectedReportPath,
    setSelectedModelPath,
    setSelectedReportPath,
    modelFolderPath,
    reportFolderPath,
    modelCheckboxFiles,
    reportCheckboxFiles,
    isLoadingModelFiles,
    isLoadingReportFiles,
  } = useProcessReportModalContext();

  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth>
        <InputLabel id="final-report-contact-label">Final Report Contact</InputLabel>
        <Select
          labelId="final-report-contact-label"
          id="final-report-contact"
          label="Final Report Contact"
          value={selectedContactKey}
          onChange={event => setSelectedContactKey(event.target.value)}
          disabled={isLoadingContacts || contacts.length === 0}
          displayEmpty
          MenuProps={{
            // Nested inside Operations Modal; keep the menu in-tree so clicks register.
            disablePortal: true,
            PaperProps: { sx: { maxHeight: 320 } },
          }}
        >
          {contacts.length === 0 && (
            <MenuItem value="" disabled>
              {isLoadingContacts ? 'Loading contacts…' : 'No contacts found'}
            </MenuItem>
          )}
          {contacts.map((contact, index) => {
            const key = contactSelectKey(contact, index);
            return (
              <MenuItem key={key} value={key}>
                {contact.name}
                {contact.email ? ` (${contact.email})` : ''}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {useS3Fallback ? (
        <Box>
          <Typography variant="subtitle2">Valuation files</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            No Dropbox folder linked — browse the Valuation directory in the backup bucket.
          </Typography>
          <WorkboardDropboxPicker
            key={initialPickerPrefix || 'root'}
            companyName={companyName}
            initialPrefix={initialPickerPrefix}
            selectedModelKey={selectedModelPath}
            selectedReportKey={selectedReportPath}
            onSelectModel={setSelectedModelPath}
            onSelectReport={setSelectedReportPath}
          />
        </Box>
      ) : (
        <>
          <Box>
            <Typography variant="subtitle2">Model Filename</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', wordBreak: 'break-all' }}
            >
              {modelFolderPath}
            </Typography>
            <FileCheckboxList
              files={modelCheckboxFiles}
              selectedPath={selectedModelPath}
              onSelect={path => setSelectedModelPath(prev => toggleExclusivePath(prev, path))}
              isLoading={isLoadingModelFiles}
              emptyLabel="No files found in the Model folder."
              idPrefix="model-file"
            />
          </Box>

          <Box>
            <Typography variant="subtitle2">Final Report File Name</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', wordBreak: 'break-all' }}
            >
              {reportFolderPath}
            </Typography>
            <FileCheckboxList
              files={reportCheckboxFiles}
              selectedPath={selectedReportPath}
              onSelect={path => setSelectedReportPath(prev => toggleExclusivePath(prev, path))}
              isLoading={isLoadingReportFiles}
              emptyLabel="No files found in the Report folder."
              idPrefix="report-file"
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export default ProcessReportInputsPanel;
